import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { SiteSettingsService } from './core/services/site-settings.service';
import { MAINTENANCE_MESSAGE, MAINTENANCE_SUBMESSAGE, MAINTENANCE_TITLE } from './maintenance-mode';

interface FilmTile {
  imageUrl: string;
}

@Component({
  selector: 'app-maintenance-page',
  standalone: true,
  imports: [CommonModule],
  template: `
    <main class="maintenance-page" aria-labelledby="maintenance-title">
      <!-- 3 Full-Height Horizontal Movie Filmstrips (Single Shared Perforations, Search-Bar Backdrop Colors) -->
      <section class="filmstrips-background" aria-hidden="true">
        <!-- Top Perforation Strip -->
        <div class="perforation-row"></div>

        <!-- Filmstrip 1: Right to Left -->
        <div class="filmstrip-viewport">
          <div class="filmstrip-track filmstrip-track--left">
            <div class="film-frame" *ngFor="let film of strip1Films">
              <img [src]="film.imageUrl" alt="" loading="lazy">
              <div class="frame-backdrop-overlay"></div>
            </div>
            <!-- Duplicated for seamless loop -->
            <div class="film-frame" *ngFor="let film of strip1Films">
              <img [src]="film.imageUrl" alt="" loading="lazy">
              <div class="frame-backdrop-overlay"></div>
            </div>
          </div>
        </div>

        <!-- Single Divider Perforation Strip between Strip 1 & 2 -->
        <div class="perforation-row"></div>

        <!-- Filmstrip 2: Left to Right -->
        <div class="filmstrip-viewport">
          <div class="filmstrip-track filmstrip-track--right">
            <div class="film-frame" *ngFor="let film of strip2Films">
              <img [src]="film.imageUrl" alt="" loading="lazy">
              <div class="frame-backdrop-overlay"></div>
            </div>
            <!-- Duplicated for seamless loop -->
            <div class="film-frame" *ngFor="let film of strip2Films">
              <img [src]="film.imageUrl" alt="" loading="lazy">
              <div class="frame-backdrop-overlay"></div>
            </div>
          </div>
        </div>

        <!-- Single Divider Perforation Strip between Strip 2 & 3 -->
        <div class="perforation-row"></div>

        <!-- Filmstrip 3: Right to Left -->
        <div class="filmstrip-viewport">
          <div class="filmstrip-track filmstrip-track--left">
            <div class="film-frame" *ngFor="let film of strip3Films">
              <img [src]="film.imageUrl" alt="" loading="lazy">
              <div class="frame-backdrop-overlay"></div>
            </div>
            <!-- Duplicated for seamless loop -->
            <div class="film-frame" *ngFor="let film of strip3Films">
              <img [src]="film.imageUrl" alt="" loading="lazy">
              <div class="frame-backdrop-overlay"></div>
            </div>
          </div>
        </div>

        <!-- Bottom Perforation Strip -->
        <div class="perforation-row"></div>
      </section>

      <!-- Centered Hero Card (Simple, Normal & Proper with Glassmorphic Transparency) -->
      <section class="hero-center-wrapper">
        <div class="maintenance-card" [class.card-exiting]="isExiting">
          <!-- Logo & Brand in Center -->
          <div class="brand-center">
            <div class="brand-logo-box">
              <img src="/assets/images/icon.png" alt="The Scenetale" class="brand-logo">
            </div>
            <div class="brand-titles">
              <span class="brand-main-title">THE SCENETALE</span>
              <span class="brand-sub-title">SANCTUARY OF THE MOVING IMAGE</span>
            </div>
          </div>

          <!-- Eyebrow Status Badge -->
          <div class="status-badge">
            <svg class="clapper-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M2 8h20" />
              <path d="M6 4l3 4" />
              <path d="M12 4l3 4" />
              <path d="M18 4l3 4" />
            </svg>
            <span>FEATURE PRESENTATION PAUSED</span>
          </div>

          <!-- Headings -->
          <h1 id="maintenance-title">{{ title }}</h1>
          <p class="message">{{ message }}</p>

          <!-- Scheduled Countdown Box (Live Time Remaining) -->
          <div class="countdown-card-section" *ngIf="hasSchedule && !isExiting" role="timer" aria-label="Estimated maintenance duration">
            <div class="countdown-eyebrow">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>SCHEDULED RE-OPENING COUNTDOWN</span>
            </div>
            <div class="countdown-digits-grid">
              <div class="timer-box">
                <span class="timer-num">{{ countdownDays }}</span>
                <span class="timer-tag">DAYS</span>
              </div>
              <span class="timer-sep">:</span>
              <div class="timer-box">
                <span class="timer-num">{{ countdownHours }}</span>
                <span class="timer-tag">HOURS</span>
              </div>
              <span class="timer-sep">:</span>
              <div class="timer-box">
                <span class="timer-num">{{ countdownMinutes }}</span>
                <span class="timer-tag">MINUTES</span>
              </div>
              <span class="timer-sep">:</span>
              <div class="timer-box highlight-box">
                <span class="timer-num">{{ countdownSeconds }}</span>
                <span class="timer-tag">SECONDS</span>
              </div>
            </div>
            <div class="countdown-footer-hint">
              <span class="pulse-live-indicator"></span>
              Auto-resumes and transitions to home page once intermission ends
            </div>
          </div>

          <!-- Live Status Bar -->
          <div class="status-bar" role="status" aria-live="polite">
            <div class="status-bar-left">
              <div class="search-icon-circle" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="7"></circle>
                  <line x1="21" y1="21" x2="16" y2="16"></line>
                </svg>
                <span class="pulse-dot"></span>
              </div>
              <span class="status-text" [title]="submessage">{{ submessage }}</span>
            </div>
            <span class="status-btn">Back soon</span>
          </div>

          <!-- Informational Badges -->
          <div class="info-badges-row">
            <span class="info-badge">🎬 System Maintenance</span>
            <span class="info-badge">⚡ Stream Optimization</span>
            <span class="info-badge">🍿 Catalog Refresh</span>
          </div>
        </div>
      </section>

      <!-- Exit Celebration Animation Overlay (Automatic Redirect to Home) -->
      <aside class="exit-curtain-overlay" *ngIf="isExiting" aria-live="assertive">
        <div class="exit-modal-card">
          <div class="exit-reel-container">
            <div class="exit-reel-halo"></div>
            <svg class="spinning-reel-icon" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <circle cx="12" cy="12" r="3"></circle>
              <line x1="12" y1="2" x2="12" y2="9"></line>
              <line x1="12" y1="15" x2="12" y2="22"></line>
              <line x1="2" y1="12" x2="9" y2="12"></line>
              <line x1="15" y1="12" x2="22" y2="12"></line>
            </svg>
          </div>
          <h2 class="exit-modal-title">Intermission Concluded!</h2>
          <p class="exit-modal-subtitle">System maintenance is complete. Raising the theatre curtains...</p>
          <div class="exit-progress-track">
            <div class="exit-progress-fill"></div>
          </div>
          <span class="exit-redirect-hint">Welcome back to The Scenetale</span>
        </div>
      </aside>
    </main>
  `,
  styles: [`
    :host {
      display: block;
      height: 100vh;
      min-height: 100vh;
      background-color: #ffffff;
      color: #0f3d32;
      font-family: var(--font-body, 'Montserrat', sans-serif);
      overflow: hidden;
    }

    .maintenance-page {
      position: relative;
      height: 100vh;
      min-height: 100vh;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px 20px;
      overflow: hidden;
      background: #ffffff;
    }

    /* ── 3 Horizontal Movie Filmstrips (Edge-to-Edge with Single Perforation Dividers) ── */
    .filmstrips-background {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      overflow: hidden;
      pointer-events: none;
      z-index: 0;
    }

    .perforation-row {
      width: 100%;
      height: 16px;
      flex-shrink: 0;
      background-color: #ffffff;
      background-repeat: repeat-x;
      background-position: left center;
      background-size: 26px 16px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='26' height='16' viewBox='0 0 26 16'%3E%3Crect x='5' y='2' width='16' height='12' rx='2.5' ry='2.5' fill='%2312b886' fill-opacity='0.28' stroke='%230ca678' stroke-width='1.2'/%3E%3C/svg%3E");
      border-top: 1px solid rgba(18, 184, 134, 0.25);
      border-bottom: 1px solid rgba(18, 184, 134, 0.25);
    }

    .filmstrip-viewport {
      flex: 1;
      min-height: 0;
      width: 100%;
      overflow: hidden;
      display: flex;
      align-items: center;
      padding: 6px 0;
      background: #ffffff;
    }

    .filmstrip-track {
      display: flex;
      align-items: center;
      gap: 14px;
      height: 100%;
      width: max-content;
      will-change: transform;
    }

    .filmstrip-track--left {
      animation: filmScrollLeft 45s linear infinite;
    }

    .filmstrip-track--right {
      animation: filmScrollRight 50s linear infinite;
    }

    @keyframes filmScrollLeft {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }

    @keyframes filmScrollRight {
      0% {
        transform: translateX(-50%);
      }
      100% {
        transform: translateX(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .filmstrip-track--left,
      .filmstrip-track--right {
        animation: none !important;
      }
    }

    .film-frame {
      position: relative;
      flex-shrink: 0;
      height: 100%;
      aspect-ratio: 16 / 9;
      border-radius: 8px;
      overflow: hidden;
      background: #f1f8f5;
      border: 1.5px solid rgba(18, 184, 134, 0.35);
    }

    .film-frame img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    /* Backdrop colors as search bar: linear-gradient(to bottom, #e6fcf5, #96f2d7, #38d9a9, #12b886) */
    .frame-backdrop-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to bottom, #e6fcf5 0%, #96f2d7 35%, #38d9a9 70%, #12b886 100%);
      opacity: 0.36;
      mix-blend-mode: multiply;
      pointer-events: none;
    }

    /* ── Centered Hero Card (Transparent with Backdrop Blur) ── */
    .hero-center-wrapper {
      position: relative;
      z-index: 2;
      width: 100%;
      max-width: 760px;
      margin: auto;
      display: flex;
      justify-content: center;
    }

    .maintenance-card {
      width: 100%;
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      border: 1px solid rgba(255, 255, 255, 0.65);
      outline: 1px solid rgba(18, 184, 134, 0.26);
      border-radius: 20px;
      padding: 36px 36px 32px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      transition: opacity 0.5s ease, transform 0.5s ease;
    }

    .maintenance-card.card-exiting {
      animation: cardExitSmooth 1.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    @keyframes cardExitSmooth {
      0% {
        opacity: 1;
        transform: scale(1);
      }
      60% {
        opacity: 0.35;
        transform: scale(0.97) translateY(-8px);
      }
      100% {
        opacity: 0;
        transform: scale(0.94) translateY(-20px);
      }
    }

    /* ── Centered Brand & Logo ──────────────────────────────────────── */
    .brand-center {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
    }

    .brand-logo-box {
      width: 68px;
      height: 68px;
      border-radius: 16px;
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(10px);
      border: 1.5px solid rgba(18, 184, 134, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px;
      box-shadow: 0 2px 8px rgba(12, 166, 120, 0.08);
    }

    .brand-logo {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .brand-titles {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
    }

    .brand-main-title {
      font-size: 1.25rem;
      font-weight: 800;
      letter-spacing: 0.06em;
      color: #0b2e25;
    }

    .brand-sub-title {
      font-size: 0.72rem;
      letter-spacing: 0.12em;
      font-weight: 700;
      color: #0ca678;
      text-transform: uppercase;
    }

    /* ── Status Badge ───────────────────────────────────────────────── */
    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 14px;
      border-radius: 999px;
      background: rgba(230, 252, 245, 0.9);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(18, 184, 134, 0.28);
      color: #0ca678;
      font-size: 0.74rem;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 12px;
    }

    .clapper-icon {
      color: #12b886;
    }

    /* ── Typography ─────────────────────────────────────────────────── */
    h1 {
      margin: 0 0 10px;
      font-size: clamp(1.85rem, 3.8vw, 2.75rem);
      line-height: 1.1;
      font-weight: 900;
      letter-spacing: -0.01em;
      color: #0b2e25;
      max-width: 660px;
    }

    .message {
      margin: 0 0 20px;
      color: #2d564d;
      font-size: clamp(0.92rem, 1.2vw, 1.05rem);
      line-height: 1.5;
      font-weight: 500;
      max-width: 580px;
    }

    /* ── Countdown Timer Box ────────────────────────────────────────── */
    .countdown-card-section {
      width: 100%;
      max-width: 540px;
      background: rgba(240, 253, 249, 0.85);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1.5px solid rgba(18, 184, 134, 0.32);
      border-radius: 16px;
      padding: 16px 20px 14px;
      margin-bottom: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      box-shadow: 0 4px 16px rgba(12, 166, 120, 0.08);
      animation: fadeInCountdown 0.4s ease-out;
    }

    @keyframes fadeInCountdown {
      from {
        opacity: 0;
        transform: translateY(6px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .countdown-eyebrow {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #0ca678;
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }

    .countdown-digits-grid {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
    }

    .timer-box {
      flex: 1;
      min-width: 58px;
      max-width: 86px;
      background: #ffffff;
      border: 1.5px solid rgba(18, 184, 134, 0.32);
      border-radius: 12px;
      padding: 10px 4px 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
      transition: border-color 0.2s ease, transform 0.2s ease;
    }

    .timer-box.highlight-box {
      border-color: #0ca678;
      background: linear-gradient(to bottom, #ffffff 50%, #e6fcf5 100%);
    }

    .timer-num {
      font-size: 1.75rem;
      font-weight: 900;
      line-height: 1;
      color: #0b2e25;
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.02em;
    }

    .timer-tag {
      font-size: 0.62rem;
      font-weight: 800;
      letter-spacing: 0.08em;
      color: #0ca678;
      text-transform: uppercase;
      margin-top: 4px;
    }

    .timer-sep {
      font-size: 1.4rem;
      font-weight: 800;
      color: #12b886;
      margin-bottom: 12px;
      opacity: 0.7;
    }

    .countdown-footer-hint {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.76rem;
      font-weight: 600;
      color: #2d564d;
    }

    .pulse-live-indicator {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #12b886;
      animation: live-pulse 1.8s infinite;
    }

    /* ── Live Status Bar ────────────────────────────────────────────── */
    .status-bar {
      width: 100%;
      max-width: 620px;
      min-height: 50px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 5px 6px 5px 16px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1.5px solid rgba(18, 184, 134, 0.28);
      margin-bottom: 20px;
    }

    .status-bar-left {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .search-icon-circle {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #e6fcf5;
      color: #0ca678;
    }

    .pulse-dot {
      position: absolute;
      top: 1px;
      right: 1px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #12b886;
      box-shadow: 0 0 0 2px #ffffff;
      animation: live-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes live-pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.4;
        transform: scale(1.2);
      }
    }

    .status-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #0f3d32;
      font-size: 0.92rem;
      font-weight: 600;
      text-align: left;
    }

    .status-btn {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 9px 20px;
      border-radius: 999px;
      color: #ffffff;
      background: linear-gradient(135deg, #12b886 0%, #0ca678 100%);
      font-size: 0.86rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      white-space: nowrap;
      user-select: none;
    }

    /* ── Info Badges ────────────────────────────────────────────────── */
    .info-badges-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    .info-badge {
      display: inline-flex;
      align-items: center;
      padding: 6px 14px;
      border-radius: 999px;
      background: #f3f8f5;
      border: 1px solid rgba(18, 184, 134, 0.2);
      color: #244b41;
      font-size: 0.8rem;
      font-weight: 600;
    }

    /* ── Exit Celebration Animation Overlay ─────────────────────────── */
    .exit-curtain-overlay {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.94);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      padding: 24px;
      animation: fadeInOverlay 0.35s ease-out forwards;
    }

    @keyframes fadeInOverlay {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .exit-modal-card {
      max-width: 480px;
      width: 100%;
      background: #ffffff;
      border-radius: 24px;
      border: 1.5px solid rgba(18, 184, 134, 0.35);
      box-shadow: 0 20px 60px rgba(12, 166, 120, 0.16);
      padding: 38px 32px 34px;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      animation: modalPopIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    @keyframes modalPopIn {
      from {
        transform: scale(0.88) translateY(18px);
        opacity: 0;
      }
      to {
        transform: scale(1) translateY(0);
        opacity: 1;
      }
    }

    .exit-reel-container {
      position: relative;
      width: 68px;
      height: 68px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 18px;
    }

    .exit-reel-halo {
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(56, 217, 169, 0.4) 0%, rgba(18, 184, 134, 0) 70%);
      animation: live-pulse 2s infinite;
    }

    .spinning-reel-icon {
      color: #0ca678;
      animation: spinReel 2s linear infinite;
    }

    @keyframes spinReel {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .exit-modal-title {
      font-size: 1.7rem;
      font-weight: 900;
      color: #0b2e25;
      margin: 0 0 8px;
      letter-spacing: -0.01em;
    }

    .exit-modal-subtitle {
      font-size: 0.94rem;
      font-weight: 500;
      color: #2d564d;
      margin: 0 0 22px;
      line-height: 1.5;
    }

    .exit-progress-track {
      width: 100%;
      height: 8px;
      border-radius: 999px;
      background: #e6fcf5;
      overflow: hidden;
      position: relative;
      margin-bottom: 12px;
      border: 1px solid rgba(18, 184, 134, 0.25);
    }

    .exit-progress-fill {
      height: 100%;
      border-radius: 999px;
      background: linear-gradient(90deg, #38d9a9 0%, #12b886 50%, #0ca678 100%);
      animation: fillProgress 1.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    @keyframes fillProgress {
      0% {
        width: 0%;
      }
      100% {
        width: 100%;
      }
    }

    .exit-redirect-hint {
      font-size: 0.8rem;
      font-weight: 700;
      color: #0ca678;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    /* ── Responsive ─────────────────────────────────────────────────── */
    @media (max-width: 680px) {
      :host {
        height: auto;
        min-height: 100vh;
        overflow-y: auto;
      }

      .maintenance-page {
        height: auto;
        min-height: 100vh;
        padding: 24px 14px;
      }

      .maintenance-card {
        padding: 26px 16px 20px;
        border-radius: 16px;
      }

      .brand-logo-box {
        width: 56px;
        height: 56px;
      }

      .brand-main-title {
        font-size: 1.1rem;
      }

      h1 {
        font-size: clamp(1.65rem, 6.5vw, 2.2rem);
      }

      .message {
        font-size: 0.9rem;
        margin-bottom: 16px;
      }

      .countdown-card-section {
        padding: 12px 14px;
      }

      .countdown-digits-grid {
        gap: 5px;
      }

      .timer-box {
        min-width: 48px;
        padding: 8px 2px 6px;
      }

      .timer-num {
        font-size: 1.45rem;
      }

      .timer-tag {
        font-size: 0.55rem;
      }

      .timer-sep {
        font-size: 1.15rem;
        margin-bottom: 8px;
      }

      .status-bar {
        flex-direction: column;
        align-items: stretch;
        border-radius: 16px;
        padding: 12px;
        gap: 10px;
      }

      .status-text {
        white-space: normal;
        text-align: left;
        line-height: 1.4;
      }

      .status-btn {
        width: 100%;
        padding: 9px 16px;
      }

      .info-badges-row {
        gap: 6px;
      }

      .info-badge {
        font-size: 0.74rem;
        padding: 5px 10px;
      }

      .exit-modal-card {
        padding: 28px 18px 24px;
      }

      .exit-modal-title {
        font-size: 1.4rem;
      }
    }
  `]
})
export class MaintenancePageComponent implements OnInit, OnDestroy {
  title = MAINTENANCE_TITLE;
  message = MAINTENANCE_MESSAGE;
  submessage = MAINTENANCE_SUBMESSAGE;

  private readonly siteSettingsService = inject(SiteSettingsService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  strip1Films: FilmTile[] = [];
  strip2Films: FilmTile[] = [];
  strip3Films: FilmTile[] = [];

  // Countdown & Scheduling
  hasSchedule = false;
  maintenanceEndTime: string | null = null;
  countdownDays = '00';
  countdownHours = '00';
  countdownMinutes = '00';
  countdownSeconds = '00';

  // Exit Animation
  isExiting = false;

  private tickerTimer: any = null;
  private pollInterval: any = null;
  private isDestroyed = false;

  private readonly staticImageUrls = [
    // Strip 1 Images (8 films)
    'https://image.tmdb.org/t/p/w780/9cqNxx0GxF0bflZmeSMuL5tnGzr.jpg',
    'https://image.tmdb.org/t/p/w780/loRmRzQXZeqG78TqZuyvSlEQfZb.jpg',
    'https://image.tmdb.org/t/p/w780/zb6fM1CX41D9rF9hdgclu0peUmy.jpg',
    'https://image.tmdb.org/t/p/w780/fm6KqXpk3M2HVveHwCrBSSBaO0V.jpg',
    'https://image.tmdb.org/t/p/w780/dMZxEdrWIzUmUoOz2zvmFuutbj7.jpg',
    'https://image.tmdb.org/t/p/w780/8rpDcsfLJypbO6vREc0547VKqEv.jpg',
    'https://image.tmdb.org/t/p/w780/7RyHsO4yDXtBv1zUU3mTpHeQ0d5.jpg',
    'https://image.tmdb.org/t/p/w780/4HodYYKEIsGOdinkGi2Ucz6X9i0.jpg',

    // Strip 2 Images (8 films)
    'https://image.tmdb.org/t/p/w780/p1F51Lvj3sMopG948F5HsBbl43C.jpg',
    'https://image.tmdb.org/t/p/w780/n5A7brJCjejceZmHyujwUTVgQNC.jpg',
    'https://image.tmdb.org/t/p/w780/5YZbUmjbMa3ClvSW1Wj3D6XGolb.jpg',
    'https://image.tmdb.org/t/p/w780/rAiYTfKGqDCRIIqo664sY9XZIvQ.jpg',
    'https://image.tmdb.org/t/p/w780/q6y0Go1tsGEsmtFryDOJo3dEmqu.jpg',
    'https://image.tmdb.org/t/p/w780/3bhkrj58Vtu7enYsRolD1fZdja1.jpg',
    'https://image.tmdb.org/t/p/w780/ow3WnR8g99q5QQNsR7vgPRgfUx3.jpg',
    'https://image.tmdb.org/t/p/w780/8Gxv8gSFCU0XGDykEGv7zR1n2ua.jpg',

    // Strip 3 Images (8 films)
    'https://image.tmdb.org/t/p/w780/fiVW06jE7z9YnO4trhaMEdclSiC.jpg',
    'https://image.tmdb.org/t/p/w780/b0PlSFdDwbyK0cf5RxwDpaOJQvQ.jpg',
    'https://image.tmdb.org/t/p/w780/rCzpDGLbOoPwLjy3OAm5NUPOTrC.jpg',
    'https://image.tmdb.org/t/p/w780/8cdWjvZQUExUUTzyp4t6EDMubfO.jpg',
    'https://image.tmdb.org/t/p/w780/gKkl37BQuKTanygYQG1pyYgLVgf.jpg',
    'https://image.tmdb.org/t/p/w780/hZkgoQYus5vegHoetLkCJzb17zJ.jpg',
    'https://image.tmdb.org/t/p/w780/arw2vcBveWOVZr6pxd9XTd1TdQa.jpg',
    'https://image.tmdb.org/t/p/w780/d5iIlFn5s0ImszYzBPb8JPIfbXD.jpg'
  ];

  constructor() {
    this.strip1Films = this.staticImageUrls.slice(0, 8).map((imageUrl) => ({ imageUrl }));
    this.strip2Films = this.staticImageUrls.slice(8, 16).map((imageUrl) => ({ imageUrl }));
    this.strip3Films = this.staticImageUrls.slice(16, 24).map((imageUrl) => ({ imageUrl }));
  }

  ngOnInit(): void {
    this.siteSettingsService.getMaintenanceStatus().subscribe({
      next: (status) => {
        if (status.maintenance_title) this.title = status.maintenance_title;
        if (status.maintenance_message) this.message = status.maintenance_message;
        if (status.maintenance_submessage) this.submessage = status.maintenance_submessage;

        if (status.maintenance_mode === false) {
          this.triggerExitTransition();
          return;
        }

        if (status.maintenance_end_time) {
          this.maintenanceEndTime = status.maintenance_end_time;
          this.startTicker();
        }

        this.startPolling();
      },
      error: () => {
        this.startPolling();
      }
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearTimers();
  }

  private startTicker(): void {
    if (this.tickerTimer) {
      clearInterval(this.tickerTimer);
      this.tickerTimer = null;
    }

    this.updateCountdown();
    this.tickerTimer = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  private updateCountdown(): void {
    if (!this.maintenanceEndTime) {
      this.hasSchedule = false;
      return;
    }

    const target = new Date(this.maintenanceEndTime).getTime();
    if (isNaN(target)) {
      this.hasSchedule = false;
      return;
    }

    const now = Date.now();
    const diff = target - now;

    if (diff <= 0) {
      this.countdownDays = '00';
      this.countdownHours = '00';
      this.countdownMinutes = '00';
      this.countdownSeconds = '00';
      this.triggerExitTransition();
      return;
    }

    this.hasSchedule = true;
    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.countdownDays = days.toString().padStart(2, '0');
    this.countdownHours = hours.toString().padStart(2, '0');
    this.countdownMinutes = minutes.toString().padStart(2, '0');
    this.countdownSeconds = seconds.toString().padStart(2, '0');
    this.cdr.detectChanges();
  }

  private startPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    // Poll every 12 seconds to sync with server status & system time
    this.pollInterval = setInterval(() => {
      if (this.isExiting || this.isDestroyed) return;
      this.siteSettingsService.getMaintenanceStatus(true).subscribe({
        next: (status) => {
          if (status.maintenance_mode === false) {
            this.triggerExitTransition();
          } else if (status.maintenance_end_time !== this.maintenanceEndTime) {
            this.maintenanceEndTime = status.maintenance_end_time || null;
            if (this.maintenanceEndTime) {
              this.startTicker();
            } else {
              this.hasSchedule = false;
              if (this.tickerTimer) {
                clearInterval(this.tickerTimer);
                this.tickerTimer = null;
              }
              this.cdr.detectChanges();
            }
          }
        },
        error: () => {}
      });
    }, 12000);
  }

  triggerExitTransition(): void {
    if (this.isExiting) return;
    this.isExiting = true;
    this.hasSchedule = false;
    this.clearTimers();
    this.cdr.detectChanges();

    // After 1400ms animation, notify SiteSettingsService and navigate to home page
    setTimeout(() => {
      this.siteSettingsService.notifyMaintenanceModeOff();
      this.router.navigate(['/']).then(() => {
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/maintenance')) {
          window.location.href = '/';
        }
      });
    }, 1400);
  }

  private clearTimers(): void {
    if (this.tickerTimer) {
      clearInterval(this.tickerTimer);
      this.tickerTimer = null;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }
}
